import { createAppSlice } from '.';
import {
  closeMultiParcelCrops as closeMultiParcelCropsApi,
  createCrop as createApiCrop,
  deleteCrop as deleteApiCrop,
  deleteParcelCropHistory,
  getCrops,
  linkMultiParcelWithCrop as linkMultiParcelWithCropApi,
  linkParcelWithCrop as linkParcelWithCropApi,
  updateCrop as updateApiCrop,
  updateParcelCropHistory,
} from '../api/api';
import { ParcelCropRangeId } from '../api/queries/crops';
import { cropQueryKeys } from '../api/queries/keys/crops';
import { queryClient } from '../api/queryClient';
import { fetchParcel, fetchParcels } from '../slices/parcels';
import { ItemWithPermissions } from '../utils/permissions';
import { PartialWithRequired } from '../utils/types';
import { ClusterId } from './clusters';
import { ParcelId } from './parcels';
import { alerting, success } from './snacks';

export type CropId = string;

type BasicCrop = ItemWithPermissions & {
  id: CropId;
  name: string;
  crop_id: number;
  color: string;
  code: string;
  locale: string;
  provenance: string;
  parcel_count?: number;
  parcel_area_sum?: number;
  cluster_id: undefined;
};

export type CustomCrop = ItemWithPermissions & {
  id: CropId;
  name: string;
  crop_id: number;
  base_crop?: string;
  color: string;
  code: '';
  locale: undefined;
  provenance: undefined;
  parcel_count?: number;
  parcel_area_sum?: number;
  cluster_id: ClusterId;
};

export type Crop = BasicCrop | CustomCrop;

type CloseCropHistoryError = {
  parcel_id: ParcelId;
  error_code: number;
  from_date: string | null;
};

export type CloseCropHistoryResult = {
  success: ParcelId[];
  errors: CloseCropHistoryError[];
};

type State = {
  cropByID: Record<CropId, Crop>;
  cropIDs: CropId[];
};

const initialState: State = {
  cropByID: {},
  cropIDs: [],
};

const cropsSlice = createAppSlice({
  name: 'crops',
  initialState,
  reducers: (create) => ({
    fetchCrops: create.asyncThunk(
      async (_, { dispatch }) => {
        const crops = (await alerting(getCrops, { dispatch })) as Crop[];
        return crops;
      },
      {
        fulfilled: (state, { payload }) => {
          for (const crop of payload) {
            state.cropByID[crop.id] = crop;
            state.cropIDs.push(crop.id);
          }
        },
      },
    ),
    createCrop: create.asyncThunk(
      async (payload: Partial<CustomCrop>, { dispatch }) => {
        const newCrop = (await alerting(() => createApiCrop(payload), { dispatch })) as CustomCrop;
        return newCrop;
      },
      {
        fulfilled: (state, { payload }) => {
          state.cropByID[payload.id] = payload;
          state.cropIDs.push(payload.id);
        },
      },
    ),
    updateCrop: create.asyncThunk(
      async (payload: PartialWithRequired<Crop, 'id'>, { dispatch }) => {
        const updatedCrop = (await alerting(() => updateApiCrop(payload.id, payload), { dispatch })) as Crop;
        return updatedCrop;
      },
      {
        fulfilled: (state, { payload }) => {
          state.cropByID[payload.id] = payload;
        },
      },
    ),
    deleteCrop: create.asyncThunk(
      async (payload: CropId, { dispatch }) => {
        await alerting(() => deleteApiCrop(payload), { dispatch });
        return payload;
      },
      {
        fulfilled: (state, { payload }) => {
          state.cropIDs = state.cropIDs.filter((id) => id !== payload);
          delete state.cropByID[payload];
        },
      },
    ),
    linkParcelWithCrop: create.asyncThunk(
      async ({ parcelId, payload }: { parcelId: ParcelId; payload: unknown }, { dispatch }) => {
        await alerting(() => linkParcelWithCropApi(parcelId, payload), { dispatch });
        success('Crop.link_ok', { dispatch });
        void queryClient.invalidateQueries({ queryKey: cropQueryKeys.parcelHistory(parcelId) });
        await dispatch(fetchParcel(parcelId));
      },
    ),
    linkMultiParcelWithCrop: create.asyncThunk(
      async ({ parcelIds, payload }: { parcelIds: ParcelId[]; payload: unknown }, { dispatch }) => {
        await alerting(() => linkMultiParcelWithCropApi(parcelIds, payload), { dispatch });
        success('Crop.link_ok', { dispatch });
        for (const parcelId of parcelIds) {
          void queryClient.invalidateQueries({ queryKey: cropQueryKeys.parcelHistory(parcelId) });
        }
        await dispatch(fetchParcels());
      },
    ),
    closeParcelCrops: create.asyncThunk(
      async ({ parcelIds, toDate }: { parcelIds: ParcelId[]; toDate: string }, { dispatch }) => {
        const result = (await alerting(() => closeMultiParcelCropsApi(parcelIds, { to_date: toDate }), {
          dispatch,
        })) as CloseCropHistoryResult;
        for (const parcelId of parcelIds) {
          void queryClient.invalidateQueries({ queryKey: cropQueryKeys.parcelHistory(parcelId) });
        }
        await dispatch(fetchParcels());
        return result;
      },
    ),
    deleteParcelCrop: create.asyncThunk(
      async (
        { parcelId, parcelCropRangeId }: { parcelId: ParcelId; parcelCropRangeId: ParcelCropRangeId },
        { dispatch },
      ) => {
        await alerting(() => deleteParcelCropHistory(parcelId, parcelCropRangeId), { dispatch });
        void queryClient.invalidateQueries({ queryKey: cropQueryKeys.parcelHistory(parcelId) });
        await dispatch(fetchParcel(parcelId));
      },
    ),
    updateParcelCrop: create.asyncThunk(
      async (
        {
          parcelId,
          parcelCropRangeId,
          payload,
        }: { parcelId: ParcelId; parcelCropRangeId: ParcelCropRangeId; payload: unknown },
        { dispatch },
      ) => {
        await alerting(() => updateParcelCropHistory(parcelId, parcelCropRangeId, payload), { dispatch });
        void queryClient.invalidateQueries({ queryKey: cropQueryKeys.parcelHistory(parcelId) });
        await dispatch(fetchParcel(parcelId));
      },
    ),
  }),
  selectors: {
    selectCropsById: (state) => state.cropByID,
    selectCropIds: (state) => state.cropIDs,
  },
});

export const {
  fetchCrops,
  createCrop,
  updateCrop,
  deleteCrop,
  linkParcelWithCrop,
  linkMultiParcelWithCrop,
  closeParcelCrops,
  deleteParcelCrop,
  updateParcelCrop,
} = cropsSlice.actions;
export const { selectCropIds, selectCropsById } = cropsSlice.selectors;

export default cropsSlice.reducer;
